import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Loader2, Trophy, Check, X, Users, Map, BookOpen, Sparkles, Wine, Gift } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function MaestroPlayer() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const initialCode = (params.get("code") || "").replace(/\D/g, "").slice(0, 6);
  const [code, setCode] = useState(initialCode);
  const [name, setName] = useState("");
  const [team, setTeam] = useState("");
  const [joined, setJoined] = useState(null); // {player_id, name, team, code}
  const [room, setRoom] = useState(null);
  const [answering, setAnswering] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [answered, setAnswered] = useState({}); // {q_idx: {points, correct}}

  // Preload room to display team dropdown before joining
  useEffect(() => {
    if (!code || code.length !== 6) return;
    api.get(`/maestro/rooms/${code}`).then((r) => setRoom(r.data)).catch(() => {});
  }, [code, joined]);

  // Polling after joining
  useEffect(() => {
    if (!joined) return;
    let alive = true;
    const tick = async () => {
      try { const r = await api.get(`/maestro/rooms/${code}`, { params: { player_id: joined.player_id } }); if (alive) setRoom(r.data); }
      catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 1200);
    const clock = setInterval(() => setNow(Date.now()), 200);
    return () => { alive = false; clearInterval(iv); clearInterval(clock); };
  }, [joined, code]);

  const join = async () => {
    if (!name.trim()) return toast.error("Entrez un pseudo");
    try {
      const r = await api.post("/maestro/rooms/join", { code, player_name: name.trim(), team_name: team || null });
      setJoined(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de rejoindre"); }
  };

  const submit = async (idx) => {
    if (answering) return;
    const q = room?.question;
    if (!q) return;
    const startedAt = q.question_started_at ? new Date(q.question_started_at).getTime() : now;
    const elapsed = (now - startedAt) / 1000;
    const timeLeft = Math.max(0, (q.timer_s || 30) - elapsed);
    setAnswering(true);
    try {
      const r = await api.post("/maestro/rooms/answer", { code, player_id: joined.player_id, answer_idx: idx, time_left_s: timeLeft });
      setAnswered((prev) => ({ ...prev, [room.current_idx]: { ...r.data, choice: idx } }));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setAnswering(false); }
  };

  // ─── Join screen ───
  if (!joined) {
    return (
      <div className="max-w-md mx-auto p-5 md:p-10 min-h-[80vh] flex flex-col justify-center" data-testid="maestro-player-join">
        <Toaster theme="light" />
        <h1 className="font-display text-3xl text-[#1F3A5F]">Rejoindre le Maestro</h1>
        <p className="text-sm text-[#4A5568] mt-1">Entrez le code à 6 chiffres puis votre pseudo.</p>
        <div className="mt-6 space-y-3">
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="Code"
            className="w-full px-4 py-3 rounded-xl border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none text-center font-mono text-2xl tracking-[0.4em]" data-testid="maestro-player-code" />
          <input value={name} onChange={(e) => setName(e.target.value.slice(0, 40))} placeholder="Votre pseudo"
            className="w-full px-4 py-3 rounded-xl border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none" data-testid="maestro-player-name" />
          {room && (room.teams || []).length > 0 && (
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#8B95A1]"><Users className="w-3 h-3 inline" /> Équipe</label>
              <select value={team} onChange={(e) => setTeam(e.target.value)}
                className="mt-1 w-full px-3 py-3 rounded-xl border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none" data-testid="maestro-player-team">
                <option value="">— Choisir une équipe —</option>
                {room.teams.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
            </div>
          )}
          <button onClick={join} data-testid="maestro-player-submit"
            className="w-full px-4 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
            Rejoindre
          </button>
        </div>
      </div>
    );
  }

  // ─── After join, waiting ───
  if (!room) return <div className="p-10 text-center text-[#8B95A1]"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  if (room.status === "lobby") {
    return (
      <div className="max-w-md mx-auto p-5 md:p-10 min-h-[80vh] flex flex-col justify-center text-center" data-testid="maestro-player-lobby">
        <div className="text-4xl">🍷</div>
        <h1 className="font-display text-2xl text-[#1F3A5F] mt-3">Bienvenue {joined.name} !</h1>
        {joined.team && <div className="text-sm text-[#4A5568] mt-1">Équipe : <b>{joined.team}</b></div>}
        <div className="mt-4 text-[#8B95A1] text-sm">En attente du lancement…</div>
        <Loader2 className="w-6 h-6 animate-spin text-[#E5276A] mx-auto mt-3" />
      </div>
    );
  }

  if (room.status === "ended") {
    const me = room.me;
    const nextUrl = encodeURIComponent(window.location.pathname + window.location.search);
    return (
      <div className="max-w-lg mx-auto p-5 md:p-8 min-h-[80vh]" data-testid="maestro-player-ended">
        <Toaster theme="light" />
        <div className="text-center">
          <Trophy className="w-12 h-12 text-[#F9C548] mx-auto" />
          <h1 className="font-display text-3xl text-[#1F3A5F] mt-3">C&apos;est fini !</h1>
          <div className="mt-4 rounded-2xl bg-white border border-[#1F3A5F]/10 p-6">
            <div className="text-[10px] uppercase tracking-widest text-[#8B95A1] font-bold">Votre score</div>
            <div className="font-display text-5xl text-[#E5276A] mt-1">{me?.score || 0} pts</div>
            {me?.name && <div className="text-xs text-[#8B95A1] mt-1">Bravo {me.name} !</div>}
          </div>
        </div>

        {/* Guest → invite to sign up. Logged-in users see a "keep exploring" card. */}
        {!user ? (
          <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#8B0000] via-[#1F3A5F] to-[#E5276A] p-6 text-white shadow-xl relative overflow-hidden" data-testid="maestro-player-signup-cta">
            <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-bold uppercase tracking-widest">
                <Gift className="w-3 h-3" /> Offert
              </div>
              <h2 className="font-display text-2xl mt-3 leading-tight">Envie d&apos;approfondir votre culture vin&nbsp;?</h2>
              <p className="text-sm text-white/85 mt-2">Créez votre compte <b>gratuit</b> geoVINUM et débloquez&nbsp;:</p>
              <ul className="mt-3 space-y-2 text-sm">
                <li className="flex items-start gap-2"><Map className="w-4 h-4 mt-0.5 shrink-0 text-[#F9C548]" /><span><b>Atlas mondial</b> — 3&nbsp;000+ appellations, cépages et producteurs</span></li>
                <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-[#F9C548]" /><span><b>geoVINUM AI</b> — un coach vin qui répond à toutes vos questions</span></li>
                <li className="flex items-start gap-2"><BookOpen className="w-4 h-4 mt-0.5 shrink-0 text-[#F9C548]" /><span><b>Academy</b> — mini-parcours certifiants dans 100+ régions</span></li>
                <li className="flex items-start gap-2"><Wine className="w-4 h-4 mt-0.5 shrink-0 text-[#F9C548]" /><span><b>Carnet de dégustation</b> — sauvegardez chaque vin dégusté</span></li>
              </ul>
              <div className="mt-5 flex flex-col gap-2">
                <Link to={`/signup?next=${nextUrl}`} data-testid="maestro-player-signup-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-[#8B0000] font-bold hover:bg-[#FCE4ED] shadow-lg">
                  Créer mon compte gratuit
                </Link>
                <Link to={`/login?next=${nextUrl}`} data-testid="maestro-player-login-btn"
                  className="w-full inline-flex items-center justify-center gap-1 px-4 py-2 text-xs text-white/85 hover:text-white underline underline-offset-4">
                  J&apos;ai déjà un compte
                </Link>
              </div>
              <p className="text-[11px] text-white/60 mt-3 text-center">
                Sans engagement · Aucune carte requise · Gratuit à vie sur le socle
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl bg-white border border-[#1F3A5F]/10 p-5 text-center" data-testid="maestro-player-explore">
            <div className="font-display text-lg text-[#1F3A5F]">Bien joué&nbsp;!</div>
            <p className="text-xs text-[#4A5568] mt-1">Continuez sur geoVINUM avec les Academy, l&apos;Atlas ou le Coach IA.</p>
            <Link to="/courses" className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
              <BookOpen className="w-4 h-4" /> Explorer les parcours
            </Link>
          </div>
        )}
      </div>
    );
  }

  const q = room.question;
  const myAnswer = answered[room.current_idx];
  const startedAt = q?.question_started_at ? new Date(q.question_started_at).getTime() : now;
  const elapsed = (now - startedAt) / 1000;
  const timeLeft = Math.max(0, (q?.timer_s || 30) - elapsed);
  const pct = Math.max(0, Math.min(100, (timeLeft / (q?.timer_s || 30)) * 100));

  return (
    <div className="max-w-md mx-auto p-5 md:p-10" data-testid="maestro-player-play">
      <Toaster theme="light" />
      <div className="text-xs font-bold uppercase tracking-widest text-[#8B95A1] text-center">
        Q {room.current_idx + 1}/{room.n_questions} · {room.me?.score || 0} pts
      </div>
      {room.status === "playing" && (
        <div className="mt-3 h-3 rounded-full bg-[#1F3A5F]/10 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#E5276A] to-[#F9C548] transition-[width] duration-200" style={{ width: `${pct}%` }} />
        </div>
      )}
      <h2 className="font-display text-xl text-[#1F3A5F] mt-4 text-center">{q?.question}</h2>
      <div className="mt-5 grid gap-3">
        {q?.choices?.map((c, i) => {
          const revealed = room.status === "revealing";
          const isCorrect = revealed && q.correct_idx === i;
          const isWrong = revealed && myAnswer?.choice === i && !isCorrect;
          const isMine = myAnswer?.choice === i;
          const bg = isCorrect ? "bg-[#4CAF50] text-white border-[#4CAF50]"
                  : isWrong  ? "bg-red-100 border-red-300 text-red-700"
                  : isMine   ? "bg-[#FCE4ED] border-[#E5276A] text-[#1F3A5F]"
                             : "bg-white border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]/40";
          return (
            <button key={i} onClick={() => submit(i)} disabled={!!myAnswer || answering || room.status !== "playing"} data-testid={`maestro-player-choice-${i}`}
              className={`p-4 rounded-xl border-2 font-display text-lg transition-colors disabled:cursor-not-allowed ${bg}`}>
              {c}
              {isCorrect && <Check className="w-4 h-4 inline ml-2" />}
              {isWrong && <X className="w-4 h-4 inline ml-2" />}
            </button>
          );
        })}
      </div>
      {myAnswer && room.status === "playing" && (
        <div className="mt-4 text-center text-sm text-[#8B95A1]">Réponse enregistrée ✓ — en attente des autres…</div>
      )}
      {myAnswer && room.status === "revealing" && (
        <div className={`mt-4 text-center text-lg font-bold ${myAnswer.correct ? "text-[#4CAF50]" : "text-red-500"}`}>
          {myAnswer.correct ? `+${myAnswer.points} pts !` : "Manqué !"}
        </div>
      )}
    </div>
  );
}
